import { Component, computed, input, InputSignal, Signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { SocialItem } from '@core/cms/landing.cms';
import { BrandIcon } from '@shared/ui/brand-icon/brand-icon';
import { CmsImage } from '@shared/ui/cms-image/cms-image';
import { CmsMedia } from '@shared/models/cms.model';

/** A `shared.social-item` resolved into everything the template needs to render one anchor. */
interface SocialLinkView {
    readonly id: string;
    readonly href: string;
    readonly image: CmsMedia;
    readonly customClass: string;
    readonly target: string;
    readonly ariaLabel: string;
    /** Set only for `_blank` links, to sever the opener reference. */
    readonly rel: string | null;
}

/**
 * Renders the "Follow Us" heading and the row of social links in the footer's contact column.
 *
 * The links come from the CMS (`socialItems` on `footer-sections.copyrigt-block`) — the icon is a
 * Strapi media field, and `label` supplies the anchor's accessible name since the link has no visible
 * text. Social links are always external, so they render as a plain `href`.
 */
@Component({
    selector: 'app-footer-socials',
    imports: [TranslatePipe, BrandIcon, CmsImage],
    templateUrl: './footer-socials.html',
    styleUrl: './footer-socials.scss',
})
export class FooterSocials {
    /** Social links to render, as authored in the CMS copyright block. */
    public readonly socials: InputSignal<ReadonlyArray<SocialItem>> =
        input.required<ReadonlyArray<SocialItem>>();

    /** Render-ready links — resolved once per input change rather than in template bindings. */
    protected readonly links: Signal<ReadonlyArray<SocialLinkView>> = computed(
        (): ReadonlyArray<SocialLinkView> =>
            this.socials().map((item: SocialItem): SocialLinkView => {
                // Strapi's GraphQL strips the leading underscore from the `_blank`/`_self` enum.
                // Binding the raw `self` would make it a *named* browsing context and open a new
                // tab, so restore the `_` (same fix as `CmsBtn`).
                const target: string = item.target === 'blank' ? '_blank' : '_self';

                return {
                    id: item.id,
                    href: item.link,
                    image: item.image,
                    customClass: item.customClass ?? '',
                    target,
                    // The anchor has no visible text, so it needs a name; fall back to the raw
                    // link when an editor leaves `label` empty (WCAG 2.4.4 / 4.1.2).
                    ariaLabel: item.label || item.link,
                    rel: target === '_blank' ? 'noopener noreferrer' : null,
                };
            }),
    );
}
